"use client";

import { useFairPrice } from "@/hooks/ai-hooks";
import { AlertTriangle, BadgeCheck, Flame, Loader2, ShieldCheck } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect } from "react";

export default function FairPriceBadge({ data }: { data: any }) {
  const locale = useLocale();
  const isAr = locale === "ar";
  const fair = useFairPrice();

  const isSale = data?.is_sale === true;
  const purpose = isSale ? "buy" : "rent";
  const rentType = data?.rent_type === "daily" ? "daily" : "yearly";
  const price = isSale
    ? data?.sale_price
    : rentType === "daily"
      ? data?.price_per_day
      : data?.price_per_year;

  useEffect(() => {
    if (data?.region && data?.propertyType && data?.area && price) {
      fair.mutate({
        region: data.region,
        propertyType: data.propertyType,
        area: data.area,
        purpose,
        rentType,
        price,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.region, data?.propertyType, data?.area, price]);

  if (!data?.region || !data?.area || !price) return null;

  if (fair.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 mt-2">
        <Loader2 size={15} className="animate-spin" />
        {isAr ? "نتحقق من عدالة السعر..." : "Checking price fairness..."}
      </div>
    );
  }

  const r = fair.data;
  if (!r) return null;

  // classify
  let verdict: { icon: any; label: string; cls: string };
  if (price <= r.low) {
    verdict = {
      icon: Flame,
      label: isAr ? "أقل من سعر السوق — صفقة! 🔥" : "Below market — great deal! 🔥",
      cls: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    };
  } else if (price <= r.high) {
    verdict = {
      icon: BadgeCheck,
      label: isAr ? "سعر عادل ومناسب ✅" : "Fair price ✅",
      cls: "bg-[#0d3589]/8 text-[#0d3589] ring-[#0d3589]/20",
    };
  } else {
    verdict = {
      icon: AlertTriangle,
      label: isAr ? "أعلى من متوسط السوق" : "Above market average",
      cls: "bg-amber-50 text-amber-700 ring-amber-200",
    };
  }
  const V = verdict.icon;
  const fmt = (n: number) => new Intl.NumberFormat(isAr ? "ar-SA" : "en-US").format(Math.round(n || 0));

  return (
    <div className="mt-3 rounded-2xl border border-slate-100 bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={16} className="text-[#0d3589]" />
        <span className="text-xs font-bold text-[#0d3589]">
          {isAr ? "ضمان السعر العادل من SNA" : "SNA Fair-Price Guarantee"}
        </span>
      </div>
      <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold ring-1 ${verdict.cls}`}>
        <V size={15} />
        {verdict.label}
      </div>
      <p className="text-xs text-slate-400 mt-2">
        {isAr ? "النطاق العادل المقدّر" : "Estimated fair range"}: {fmt(r.low)} — {fmt(r.high)}{" "}
        {isAr ? "ريال" : "SAR"}
        {r.comparables ? (isAr ? ` · بناءً على ${r.comparables} عقار مشابه` : ` · based on ${r.comparables} comps`) : ""}
      </p>
    </div>
  );
}
